import styles from './basic-customer-details.module.scss';
import { Col, Row } from 'reactstrap';
import { useState } from 'react';
import { AvatarOnlyBorderSecondary, Card, CardContent, CardHeader, CheckboxLabel, Divider, Icon, IconButton, Link, OrderTypesEnum, Tooltip, Typography } from '@frontend-workspace/react-shared-lib';
import EditCompanyDetailsDialog from '../../edit-company-details-dialog/edit-company-details-dialog';
import { ICompanyUserAccounts } from '../../_models/company-user-accounts';
import { ICompanyDetails } from '../../_models/company-details';
import { useTranslation } from 'react-i18next';
import { UseSignalRHook } from '../../signalr-context/signalr-context';

/* eslint-disable-next-line */
export interface BasicCustomerDetailsProps {
  company: ICompanyDetails,
  userAccounts: ICompanyUserAccounts[],
  getCompany: () => void
}

export function BasicCustomerDetails(props: BasicCustomerDetailsProps) {
  const { t } = useTranslation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const toggleCompanyDetailsDialogOpen = () => setIsDialogOpen(!isDialogOpen);

  UseSignalRHook(props.getCompany, [OrderTypesEnum.ChangeCompanyInfo]);

  return (<>
    <Row className='mb-4'>
      <Col md={6} xxl={3}>
        <Card className='mb-4'>
          <CardHeader
            avatar={
              <AvatarOnlyBorderSecondary>
                <Icon>business</Icon>
              </AvatarOnlyBorderSecondary>
            }
            title={t('standard.customerDetails')}
          />
          <CardContent className='pt-0' sx={{ minHeight: 100 }}>
            <div className='d-flex mb-3'>
              <div>
                <Typography
                  className='MuiTypography-body6'
                  component="div">
                  {t('standard.customerNo')}
                </Typography>
                <Typography
                  className='MuiTypography-body5'
                  component="div">
                  {props.company?.companyNumber}
                </Typography>
              </div>
              <Divider orientation="vertical" variant="fullWidth" flexItem className="mx-4" />
              <div>
                <Typography
                  className='MuiTypography-body6'
                  component="div">
                  {t('standard.organisationNumber')}
                </Typography>
                <Typography
                  className='MuiTypography-body5'
                  component="div">
                  {props.company?.orgNr}
                </Typography>
              </div>

              <Divider orientation="vertical" variant="fullWidth" flexItem className="mx-4" />
              <div>
                <Typography
                  className='MuiTypography-body6'
                  component="div">
                  {t('standard.payerCode')}
                </Typography>
                <Typography
                  className='MuiTypography-body5'
                  component="div">
                  {props.company?.payerCode}
                </Typography>
              </div>
            </div>
            <Tooltip
              placement='top-start'
              title={props.company?.phone}>
              <Typography
                component="div"
                className='MuiTypography-body5 text-truncate'>
                {t('standard.phone')}: {props.company?.phone}
              </Typography>
            </Tooltip>
          </CardContent>
        </Card>
      </Col>
      <Col md={6} xxl={3}>
        <Card className='mb-4'>
          <CardHeader
            avatar={
              <AvatarOnlyBorderSecondary>
                <Icon>place</Icon>
              </AvatarOnlyBorderSecondary>
            }
            title={t('standard.address')}
          />
          <CardContent className='pt-0' sx={{ minHeight: 100 }}>
            <Tooltip
              placement='top-start'
              title={props.company?.address}>
              <Typography
                component="div"
                className='MuiTypography-body5 text-truncate'>
                {props.company?.address}
              </Typography>
            </Tooltip>
            <Tooltip
              placement='top-start'
              title={`${props.company?.postalCode} ${props.company?.city}`}>
              <Typography
                component="div"
                className='MuiTypography-body5 text-truncate'>
                {props.company?.postalCode} {props.company?.city}
              </Typography>
            </Tooltip>
            <Tooltip
              placement='top-start'
              title={props.company?.country}>
              <Typography
                component="div"
                className='MuiTypography-body5 text-truncate'>
                {props.company?.country}
              </Typography>
            </Tooltip>            
          </CardContent>
        </Card>
      </Col>
      <Col md={6} xxl={3}>
        <Card className='mb-4'>
          <CardHeader
            avatar={
              <AvatarOnlyBorderSecondary>
                <Icon>person</Icon>
              </AvatarOnlyBorderSecondary>
            }
            title={t('basicCustomerDetails.customerContact')}
          />
          <CardContent className='pt-0' sx={{ minHeight: 100 }}>
            {props.company && <>
              <Tooltip
                placement='top-start'
                title={`${props.company.companyContact?.firstName} ${props.company.companyContact?.lastName}`}>
                <Typography
                  component="div"
                  className='MuiTypography-body5 text-truncate'>
                  {props.company.companyContact?.firstName} {props.company.companyContact?.lastName}
                </Typography>
              </Tooltip>
              <Link
                className='MuiTypography-body5 text-truncate mb-1'
                href={`mailto:${props.company.companyContact?.email}`}
                underline='always'
                target="_blank">
                {props.company.companyContact?.email}
              </Link>
              <Typography
                component="div"
                className='MuiTypography-body5'>
                {props.company.companyContact?.phoneNumber}
              </Typography>
            </>}
          </CardContent>
        </Card>
      </Col>
      <Col md={6} xxl={3}>
        <Card className='mb-4'>
          <CardHeader
            avatar={
              <AvatarOnlyBorderSecondary>
                <Icon>settings</Icon>
              </AvatarOnlyBorderSecondary>
            }
            action={
              <IconButton onClick={toggleCompanyDetailsDialogOpen}>
                <Icon>edit</Icon>
              </IconButton>
            }
            title={t('basicCustomerDetails.companySettings')}
            sx={{
              '& .MuiCardHeader-content': {
                display: 'block',
                overflow: 'hidden',
              },
            }}
            titleTypographyProps={{
              noWrap: true,
              textOverflow: 'ellipsis',
            }}
          />
          <CardContent className='pt-0' sx={{ minHeight: 100 }}>
            <div className='d-flex mb-1'>
              <div className='text-truncate'>
                <Typography
                  component="div"
                  className='MuiTypography-body6 text-truncate'>
                  {t('basicCustomerDetails.administrator')}
                </Typography>
                <Tooltip
                  placement='top-start'
                  title={props.company?.admin ? `${props.company?.admin?.firstName} ${props.company?.admin?.lastName}` : t('standard.none')}>
                  <Typography
                    component="div"
                    className='MuiTypography-body5 text-truncate'>
                    {props.company?.admin ? `${props.company?.admin?.firstName} ${props.company?.admin?.lastName}` : t('standard.none')}
                  </Typography>
                </Tooltip>
              </div>
              <Divider orientation="vertical" variant="fullWidth" flexItem className="mx-4" />
              <div className='text-truncate'>
                <Typography
                  component="div"
                  className='MuiTypography-body6 text-truncate'>
                  {t('basicCustomerDetails.language')}
                </Typography>
                <Typography
                  component="div"
                  className='MuiTypography-body5 text-truncate'>
                  {props.company?.language}
                </Typography>
              </div>
            </div>
            <div className='d-flex'>
              <div>
                <CheckboxLabel
                  label={t('basicCustomerDetails.requiresTwoFactorAuthentication')}
                  disabled
                  checked={props.company?.twoFactorAuthentication || false}
                  size="small"
                />
              </div>
              <div>
                <CheckboxLabel
                  label={t('standard.domain')}
                  disabled
                  checked={props.company?.automaticallyApprovedDomains?.length > 0}
                  size="small"
                />
              </div>
            </div>
            {props.company?.federatedDomain && (
              <div>{t('basicCustomerDetails.federatedDomain')}: {props.company.federatedDomain}</div>
            )}
          </CardContent>
        </Card>
      </Col>
    </Row>
    {isDialogOpen && <EditCompanyDetailsDialog
      company={props.company}
      onClose={toggleCompanyDetailsDialogOpen}
      coAdmins={props.company.coAdmins}
      userAccounts={props.userAccounts}
      admin={props.company.admin}
    />
    }
  </>);
}

export default BasicCustomerDetails;